import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeftIcon,
  DocumentTextIcon,
  PrinterIcon,
} from "@heroicons/react/24/outline";
import { Form, Formik } from "formik";
import toast from "react-hot-toast";
import { HsePageChrome } from "../../../../../components/dashboard/hse/HsePageChrome";
import { PMField } from "../../../../../components/utils/PMField";
import { FormikSaveBar } from "../../../../../components/utils/FormikSaveBar";
import { ResourceLoading } from "../../../../../components/utils/ResourceLoading";
import { FormDocumentRenderer } from "../../../../../components/dashboard/document-forms/FormDocumentRenderer";
import {
  createDocumentForm,
  fetchDocumentForm,
  fetchDocumentFormPdf,
  updateDocumentForm,
} from "../../../../../api/documentForms";

export const DocumentFormPage = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(Boolean(id));
  const [post, setPost] = useState(null);
  const [isPrinting, setIsPrinting] = useState(false);

  useEffect(() => {
    if (!id) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    fetchDocumentForm(id).then((data) => {
      setPost(data?.id ? data : null);
      setIsLoading(false);
    });
  }, [id]);

  const save = async (values) => {
    const payload = {
      title: values.title,
      values: values.values,
      status: values.status,
      include_signature: values.include_signature,
    };

    const res = post?.id
      ? await updateDocumentForm(post.id, payload)
      : await createDocumentForm(payload);

    if (res?.errors) {
      toast.error(Array.isArray(res.errors) ? res.errors.join(", ") : t("documentForms.save_failed"));
      return;
    }

    toast.success(t("Saved"));
    setPost(res);
    const formId = res?.id ?? res?.data?.id;
    if (!post?.id && formId) {
      navigate(`/dashboard/hse-ks/documents/form-archive/${formId}`, { replace: true });
    }
  };

  const handlePrint = async () => {
    if (!post?.id) {
      toast.error(t("documentForms.save_before_print"));
      return;
    }
    setIsPrinting(true);
    const res = await fetchDocumentFormPdf(post.id, {
      signature: post.include_signature,
    });
    setIsPrinting(false);
    if (res?.pdf_url) {
      window.open(res.pdf_url, "_blank", "noopener");
    } else {
      toast.error(t("documentForms.print_failed"));
    }
  };

  if (isLoading) {
    return (
      <HsePageChrome title={t("documentForms.edit")}>
        <ResourceLoading />
      </HsePageChrome>
    );
  }

  const readOnly = post?.status === "final";

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-5xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="mb-8 rounded-[2rem] border border-slate-200 bg-white px-6 py-6 shadow-sm">
          <Link
            to="/dashboard/hse-ks/documents/form-archive"
            className="mb-4 inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
          >
            <ArrowLeftIcon className="h-4 w-4" />
            {t("Back")}
          </Link>
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold uppercase tracking-[0.2em] text-slate-600">
                <DocumentTextIcon className="h-4 w-4" />
                {post?.id ? t("documentForms.edit") : t("documentForms.new")}
              </div>
              <h1 className="text-3xl font-semibold tracking-tight text-slate-950">
                {post?.display_reference ? (
                  <>
                    <span className="mr-2 inline-block rounded-full bg-slate-900 px-3 py-1 text-base font-bold text-white">
                      {post.display_reference}
                    </span>
                    <span className="text-xl font-medium text-slate-600">{post?.title}</span>
                  </>
                ) : (
                  post?.title || t("documentForms.new")
                )}
              </h1>
            </div>
            {post?.id ? (
              <button
                type="button"
                disabled={isPrinting}
                onClick={handlePrint}
                className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700"
              >
                <PrinterIcon className="h-4 w-4" />
                {isPrinting ? t("Loading") : t("documentForms.print")}
              </button>
            ) : null}
          </div>
        </div>

        <Formik
          initialValues={{
            id: post?.id || null,
            title: post?.title || "",
            values: post?.values || {},
            status: post?.status || "draft",
            include_signature: post?.include_signature || false,
          }}
          enableReinitialize
          onSubmit={save}
        >
          {(formik) => (
            <Form>
              <div className="mx-auto max-w-3xl rounded-[2rem] border border-slate-200 bg-white px-8 py-10 shadow-sm">
                <div className="mb-8 space-y-4">
                  <PMField
                    name="title"
                    type="text"
                    label={t("Title")}
                    disabled={readOnly}
                    {...formik}
                  />
                  {!readOnly ? (
                    <label className="flex items-center gap-2 text-sm text-slate-700">
                      <input
                        type="checkbox"
                        checked={formik.values.include_signature}
                        onChange={(e) =>
                          formik.setFieldValue("include_signature", e.target.checked)
                        }
                      />
                      {t("documentForms.include_signature")}
                    </label>
                  ) : null}
                </div>

                <FormDocumentRenderer
                  blocks={post?.schema_snapshot || []}
                  values={formik.values.values}
                  readOnly={readOnly}
                  onChange={(next) => formik.setFieldValue("values", next)}
                />
              </div>

              {!readOnly ? (
                <div className="mx-auto mt-6 flex max-w-3xl justify-end gap-2">
                  <button
                    type="button"
                    onClick={async () => {
                      await formik.setFieldValue("status", "final");
                      formik.submitForm();
                    }}
                    className="rounded-full border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-800"
                  >
                    {t("documentForms.mark_final")}
                  </button>
                </div>
              ) : null}

              {!readOnly ? (
                <FormikSaveBar {...formik} disabled={formik.isSubmitting} />
              ) : null}
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
};
